import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from '@amzn/alchemy-components-react';
import { formatExpiryLabel, humanizeAdminErrorCode, humanizeMfaStatus } from "src/common/util";
import { BulkActionOutcome } from "src/common/enums";
import type { BulkActionResult, UseBulkManageScan2AuthResult } from "src/hooks/use-bulk-manage-scan2auth";
import { StepFrame } from "src/components/modals/bulk-manage-credentials/step-frame";
import { expandableProps } from "src/components/modals/bulk-manage-credentials/expandable";
import { actionLabel } from "src/components/modals/bulk-manage-credentials/copy";
import { logger } from "src/logger";

interface ResultsStepProps {
    /** The wizard's view model (see SelectActionStep for why it's passed whole). */
    bulk: UseBulkManageScan2AuthResult

    /** Closes the wizard and clears its state, so the next opening starts clean. */
    onDone: () => void
}

/**
 * Step 3: per-principal outcomes. Failures are listed first, then successes, then the skipped group
 * behind a collapse — the rows an operator has to act on shouldn't be below the ones they don't.
 * Registration codes are shown outright. They were masked behind per-row and show-all toggles, but the
 * operator's next task is to get every code out of here and distributed, so a reveal step stood between
 * them and the only reason they are on this screen. The row still has to be expanded, which keeps codes
 * off the screen until asked for.
 */
export const ResultsStep = (props: ResultsStepProps) => {
    const { t } = useTranslation();
    const {
        selectedAction,
        results,
        expandedUsers,
        toggleExpand,
        skippedExpanded,
        setSkippedExpanded,
    } = props.bulk;

    // Which rows the operator has already copied. Local rather than in the hook because it says
    // nothing about the run — it is a reading aid for working down a list of up to 50 codes, and it
    // doubles as a progress marker. Never cleared: a copied row stays marked while the step is open.
    const [copied, setCopied] = useState<Set<string>>(new Set());

    if (!selectedAction) {
        return null;
    }

    const copyCode = async (principalArn: string, regCode: string): Promise<void> => {
        try {
            await navigator.clipboard.writeText(regCode);
            setCopied(prev => new Set(prev).add(principalArn));
        } catch (e) {
            // Clipboard access needs a secure context and can be refused outright. The code is on
            // screen either way, so log and leave the button unmarked rather than claiming success.
            logger.error('Failed to copy a registration code to the clipboard', e as Error);
        }
    };

    const succeeded = results.filter(r => r.outcome === BulkActionOutcome.SUCCESS);
    const failed = results.filter(r => r.outcome === BulkActionOutcome.FAILED);
    // Targets the backend never confirmed. Listed above the failures because they're the only rows the
    // operator must not simply retry — the write may already have applied. See BulkActionResult.outcome.
    const unknown = results.filter(r => r.outcome === BulkActionOutcome.UNKNOWN);
    const skipped = results.filter(r => r.outcome === BulkActionOutcome.SKIPPED);

    const renderResultRow = (result: BulkActionResult) => {
        const isExpanded = expandedUsers.has(result.user.principalArn);
        const needsAttention = result.outcome === BulkActionOutcome.FAILED || result.outcome === BulkActionOutcome.UNKNOWN;
        const icon = result.outcome === BulkActionOutcome.SUCCESS ? 'checkSolid' : needsAttention ? 'warning' : 'infoSolid';
        const iconClass = result.outcome === BulkActionOutcome.SUCCESS ? 'c-success-alert' : needsAttention ? 'c-error-alert' : 'text-muted';

        return (
            <div key={result.user.principalArn} className="mb-1">
                <div
                    className="d-flex align-items-center p-1"
                    style={{ cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                    {...expandableProps(isExpanded, () => toggleExpand(result.user.principalArn))}
                >
                    <Icon icon={isExpanded ? 'caretDown' : 'caretRight'} className="mr-1" />
                    <Icon icon={icon} className={`mr-1 ${iconClass}`} />
                    <span>{result.user.fullName}</span>
                </div>
                {isExpanded && (
                    <div className="ml-4 pl-2 py-1" style={{ fontSize: '0.85rem', borderLeft: '2px solid #dee2e6' }}>
                        {result.outcome === BulkActionOutcome.SUCCESS && (
                            <>
                                {/* Only show the reg-code row when the operation issued one. Some
                                    successes (e.g. Cancel Enrollment) return no code. */}
                                {result.regCode && (
                                    <div className="d-flex align-items-center">
                                        <strong className="mr-1">{t('bulk-manage-credentials-reg-code-label')}</strong>
                                        <code className="mr-2">{result.regCode}</code>
                                        {/* stopPropagation because this sits inside the row's own expand
                                            handler — without it the code would be copied and the row
                                            would collapse in the same click. */}
                                        <Button
                                            id={`bulk-copy-code-${result.user.principalArn}`}
                                            size="sm"
                                            variant="base"
                                            icon="copy"
                                            label={copied.has(result.user.principalArn)
                                                ? t('bulk-manage-credentials-code-copied')
                                                : t('bulk-manage-credentials-copy-code')}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                void copyCode(result.user.principalArn, result.regCode as string);
                                            }}
                                        />
                                    </div>
                                )}
                                <div><strong>{t('bulk-manage-credentials-expiry-label')}</strong> {formatExpiryLabel(t, result.expiry)}</div>
                                <div><strong>{t('bulk-manage-credentials-new-status-label')}</strong> {result.newStatus ? humanizeMfaStatus(t, result.newStatus) : '—'}</div>
                            </>
                        )}
                        {result.outcome === BulkActionOutcome.FAILED && (
                            <>
                                <div className="c-error-alert"><strong>{t('bulk-manage-credentials-error-label')}</strong> {humanizeAdminErrorCode(t, result.errorCode)}</div>
                                <div><strong>{t('bulk-manage-credentials-current-status-label')}</strong> {humanizeMfaStatus(t, result.user.currentStatus, result.wasExpired)}</div>
                            </>
                        )}
                        {result.outcome === BulkActionOutcome.UNKNOWN && (
                            <>
                                <div className="c-error-alert">
                                    <strong>{t('bulk-manage-credentials-unknown-label')}</strong>{' '}
                                    {t('bulk-manage-credentials-unknown-detail')}
                                </div>
                                {/* The status shown is the one from BEFORE the run, which is exactly the
                                    thing the operator now has to re-check. */}
                                <div><strong>{t('bulk-manage-credentials-current-status-label')}</strong> {humanizeMfaStatus(t, result.user.currentStatus, result.wasExpired)}</div>
                            </>
                        )}
                        {result.outcome === BulkActionOutcome.SKIPPED && (
                            <div>
                                <strong>{t('bulk-manage-credentials-reason-label')}</strong>{' '}
                                {result.skipReason === 'credential-type'
                                    ? t('bulk-manage-credentials-reason-other-credential-type')
                                    : t('bulk-manage-credentials-reason-incompatible', { status: humanizeMfaStatus(t, result.user.currentStatus, result.wasExpired) })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Whether any code was issued at all: Cancel Enrollment succeeds without one.
    const anyCodeIssued = succeeded.some(r => r.regCode);

    return (
        <StepFrame title={t('bulk-manage-credentials-results-title', { action: actionLabel(t, selectedAction) })}>
            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    {/* One key carries all four counts. Concatenating a second key on for `unknown` left
                        the separator and the clause order in the code, where a translator can't reach
                        them; an always-present "0 unknown" is the cheaper trade. */}
                    <p className="mb-0 text-muted">
                        {t('bulk-manage-credentials-results-summary', {
                            succeeded: succeeded.length,
                            failed: failed.length,
                            unknown: unknown.length,
                            skipped: skipped.length,
                        })}
                    </p>
                </div>

                {/* A callout, not small print: this is the operator's only chance to take the codes
                    away, and muted 0.85rem text under a summary line is exactly what gets skipped. */}
                {anyCodeIssued && (
                    <div
                        className="mb-3 p-2"
                        style={{
                            border: '1px solid var(--color-base-warning-alert, #ffb700)',
                            borderRadius: '4px',
                            background: 'var(--color-base-warning-notification, #ffe6d6)',
                        }}
                    >
                        <Icon icon="warning" className="mr-1" />
                        <strong>{t('bulk-manage-credentials-codes-shown-once-title')}</strong>{' '}
                        {t('bulk-manage-credentials-codes-shown-once')}
                    </div>
                )}

                <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                    {/* Unconfirmed first (they need verifying, not retrying), then failures, then
                        successes, then skipped (collapsible). */}
                    {unknown.map(renderResultRow)}
                    {failed.map(renderResultRow)}
                    {succeeded.map(renderResultRow)}
                    {skipped.length > 0 && (
                        <div className="mt-2 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                            <div
                                className="text-muted mb-1"
                                style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                                {...expandableProps(skippedExpanded, () => setSkippedExpanded(prev => !prev))}
                            >
                                <Icon icon={skippedExpanded ? 'caretDown' : 'caretRight'} className="mr-1" />
                                <strong>{t('bulk-manage-credentials-skipped-group', { count: skipped.length })}</strong>
                            </div>
                            {skippedExpanded && skipped.map(renderResultRow)}
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-end mt-3">
                    <Button
                        id="bulk-results-done"
                        size="lg"
                        label={t('bulk-manage-credentials-done')}
                        onClick={props.onDone}
                    />
                </div>
            </div>
        </StepFrame>
    );
};
